import type { ExamScore } from '@/types';
import { parseScore } from '@/lib/scores';

export interface ScorePoint {
  key: string;
  label: string;
  shortLabel: string;
  score: number;
  mock: boolean;
}

export interface SubjectHistory {
  subject: string;
  points: ScorePoint[];
  latest: number;
  previous: number | null;
  delta: number | null;
  overallDelta: number | null;
  recentOrder: number;
}

export interface ComparisonRow {
  subject: string;
  current: number;
  previous: number | null;
  delta: number | null;
}

export interface ExamComparison {
  currentLabel: string;
  previousLabel: string | null;
  rows: ComparisonRow[];
  avgCurrent: number | null;
  avgPrevious: number | null;
  avgDelta: number | null;
}

export interface ScoreSummary {
  rising: SubjectHistory[];
  falling: SubjectHistory[];
  steady: SubjectHistory[];
  average: number | null;
  bestSubject: string | null;
  weakestSubject: string | null;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function sessionKey(s: ExamScore): string {
  const isMock = s.exam_type === '모의고사';
  const semester = isMock ? '' : s.semester;
  const month = isMock ? (s.month ?? '') : '';
  return [s.exam_type, s.year, s.grade, semester, month].join('|');
}

function sessionOrder(s: ExamScore): number {
  const year = Number(s.year) || 0;
  if (s.exam_type === '모의고사') {
    const month = Number((s.month ?? '').replace(/[^\d]/g, '')) || 0;
    return year * 1000 + month * 10;
  }
  const semester = s.semester === '2학기' ? 90 : 30;
  const exam = s.exam_type === '기말고사' ? 30 : 0;
  return year * 1000 + semester + exam;
}

export function sessionLabel(s: ExamScore): string {
  if (s.exam_type === '모의고사') return `${s.year}년 ${s.month ?? ''} 모의고사`;
  return `${s.year}년 ${s.semester} ${s.exam_type}`;
}

export function sessionShortLabel(s: ExamScore): string {
  const year = String(s.year ?? '').replace(/[^\d]/g, '').slice(-2);
  if (s.exam_type === '모의고사') {
    return `${year}·${(s.month ?? '').replace(/[^\d]/g, '')}모`;
  }
  const exam = s.exam_type === '기말고사' ? '기말' : '중간';
  const sem = s.semester === '2학기' ? '2' : '1';
  return `${year}·${sem}${exam}`;
}

export function buildSubjectHistories(scores: ExamScore[]): SubjectHistory[] {
  const bySubject = new Map<string, Map<string, { order: number; point: ScorePoint }>>();

  scores.forEach((s) => {
    const subject = (s.subject ?? '').trim();
    if (!subject) return;
    const value = parseScore(s.score);
    if (value === null) return;

    const key = sessionKey(s);
    const order = sessionOrder(s);
    let sessions = bySubject.get(subject);
    if (!sessions) {
      sessions = new Map();
      bySubject.set(subject, sessions);
    }
    const existing = sessions.get(key);
    if (!existing || order >= existing.order) {
      sessions.set(key, {
        order,
        point: {
          key,
          label: sessionLabel(s),
          shortLabel: sessionShortLabel(s),
          score: value,
          mock: s.exam_type === '모의고사',
        },
      });
    }
  });

  const histories: SubjectHistory[] = [];
  bySubject.forEach((sessions, subject) => {
    const sorted = Array.from(sessions.values()).sort((a, b) => a.order - b.order);
    const points = sorted.map((item) => item.point);
    const latest = points[points.length - 1].score;
    const previous = points.length > 1 ? points[points.length - 2].score : null;
    const first = points[0].score;
    histories.push({
      subject,
      points,
      latest,
      previous,
      delta: previous === null ? null : round1(latest - previous),
      overallDelta: points.length > 1 ? round1(latest - first) : null,
      recentOrder: sorted[sorted.length - 1].order,
    });
  });

  histories.sort(
    (a, b) => b.recentOrder - a.recentOrder || a.subject.localeCompare(b.subject, 'ko'),
  );
  return histories;
}

export function buildExamComparison(scores: ExamScore[]): ExamComparison | null {
  const sessions = new Map<
    string,
    { order: number; label: string; scores: Map<string, number> }
  >();

  scores.forEach((s) => {
    const subject = (s.subject ?? '').trim();
    if (!subject) return;
    const value = parseScore(s.score);
    if (value === null) return;

    const key = sessionKey(s);
    let session = sessions.get(key);
    if (!session) {
      session = { order: sessionOrder(s), label: sessionLabel(s), scores: new Map() };
      sessions.set(key, session);
    }
    session.scores.set(subject, value);
  });

  const list = Array.from(sessions.values()).sort((a, b) => a.order - b.order);
  if (list.length === 0) return null;

  const current = list[list.length - 1];
  const previous = list.length > 1 ? list[list.length - 2] : null;

  const rows: ComparisonRow[] = Array.from(current.scores.entries()).map(([subject, value]) => {
    const prev = previous?.scores.get(subject) ?? null;
    return {
      subject,
      current: value,
      previous: prev,
      delta: prev === null ? null : round1(value - prev),
    };
  });
  rows.sort((a, b) => a.subject.localeCompare(b.subject, 'ko'));

  const currentVals = Array.from(current.scores.values());
  const prevVals = previous ? Array.from(previous.scores.values()) : [];
  const comparable = rows.filter((r) => r.previous !== null);
  const avgDelta = comparable.length
    ? round1(comparable.reduce((acc, r) => acc + (r.delta ?? 0), 0) / comparable.length)
    : null;

  return {
    currentLabel: current.label,
    previousLabel: previous?.label ?? null,
    rows,
    avgCurrent: currentVals.length
      ? round1(currentVals.reduce((a, b) => a + b, 0) / currentVals.length)
      : null,
    avgPrevious: prevVals.length
      ? round1(prevVals.reduce((a, b) => a + b, 0) / prevVals.length)
      : null,
    avgDelta,
  };
}

export function buildScoreSummary(histories: SubjectHistory[]): ScoreSummary {
  const withDelta = histories.filter((h) => h.delta !== null);
  const rising = withDelta
    .filter((h) => (h.delta ?? 0) > 0)
    .sort((a, b) => (b.delta ?? 0) - (a.delta ?? 0));
  const falling = withDelta
    .filter((h) => (h.delta ?? 0) < 0)
    .sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0));
  const steady = withDelta.filter((h) => h.delta === 0);

  const vals = histories.map((h) => h.latest);
  const average = vals.length ? round1(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
  const sorted = [...histories].sort((a, b) => b.latest - a.latest);

  return {
    rising,
    falling,
    steady,
    average,
    bestSubject: sorted[0]?.subject ?? null,
    weakestSubject: sorted[sorted.length - 1]?.subject ?? null,
  };
}